import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    AlertTriangle, Check, ChevronRight, Clock, FolderPlus, HardDrive, Link2, Loader2, Paperclip, Search, Users
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useAccessToken } from "@/auth/useAccessToken";
import {
    canDownloadItem, canShareItem, driveApi, formatDriveBytes, type DriveFolderRef, type DriveItem
} from "@/api/driveApi";
import { FileTypeIcon } from "./FileTypeIcon";

/**
 * Outlook's "Browse cloud locations" / Gmail's "Insert files using Drive".
 *
 * Two shapes share one dialog:
 *   - `mode="files"`  — tabs Recent / My Drive / Shared with me, a search box,
 *     multi-select, and a per-dialog "Share as link" / "Attach as a copy"
 *     choice (remembered). Every pick is resolved to the action it can
 *     actually take — a view-only file can't be shared, a no-download or
 *     >25 MB file can't be copied — and the footer says why before Insert.
 *   - `mode="folder"` — My Drive folders only, "New folder", "Save here".
 *     Used by the reading pane's Save to Drive.
 *
 * Built on the Radix dialog, so focus trap, Esc and focus return come free;
 * the list itself is arrow-key navigable.
 */

export type DriveAttachMode = "link" | "copy";

export interface DrivePick {
    item: DriveItem;
    action: "link" | "copy" | "blocked";
    /** Set when the action differs from the chosen mode, or it's blocked. */
    reason?: string;
}

type Tab = "recent" | "mine" | "shared";

interface CommonProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

interface FilesProps extends CommonProps {
    mode: "files";
    /** Mail's per-file attachment ceiling — bigger files can only go as links. */
    attachLimitBytes: number;
    onPickFiles: (picks: DrivePick[]) => void;
}

interface FolderProps extends CommonProps {
    mode: "folder";
    title?: string;
    description?: string;
    confirmLabel?: string;
    onPickFolder: (folder: { id: string | null; name: string }) => void;
}

export type DriveFilePickerProps = FilesProps | FolderProps;

const MODE_KEY = "athavita.mail.driveAttachMode";

function loadMode(): DriveAttachMode {
    try {
        return window.localStorage.getItem(MODE_KEY) === "copy" ? "copy" : "link";
    } catch {
        return "link";
    }
}

function saveMode(mode: DriveAttachMode) {
    try {
        window.localStorage.setItem(MODE_KEY, mode);
    } catch {
        // private window / storage blocked — the choice just isn't remembered
    }
}

/** What Mail will actually do with a picked file under the chosen mode. */
export function resolveDrivePick(item: DriveItem, mode: DriveAttachMode, limitBytes: number): DrivePick {
    const limitMb = Math.round(limitBytes / (1024 * 1024));
    const sharable = canShareItem(item);
    const downloadable = canDownloadItem(item);
    const tooBig = item.sizeBytes > limitBytes;

    if (mode === "link") {
        if (sharable) return { item, action: "link" };
        if (downloadable && !tooBig) {
            return { item, action: "copy", reason: "You have view-only access, so it can't be shared — attaching a copy instead." };
        }
        return {
            item,
            action: "blocked",
            reason: tooBig
                ? `You have view-only access and it's over ${limitMb} MB, so it can't be shared or attached.`
                : "You have view-only access and the owner turned off downloads."
        };
    }
    if (tooBig) {
        return sharable
            ? { item, action: "link", reason: `Larger than ${limitMb} MB — sharing it as a link.` }
            : { item, action: "blocked", reason: `Larger than ${limitMb} MB, and you have view-only access so it can't be shared.` };
    }
    if (!downloadable) {
        return sharable
            ? { item, action: "link", reason: "The owner turned off downloads — sharing it as a link." }
            : { item, action: "blocked", reason: "The owner turned off downloads." };
    }
    return { item, action: "copy" };
}

export function DriveFilePicker(props: DriveFilePickerProps) {
    const { open, onOpenChange } = props;
    const isFolderMode = props.mode === "folder";
    const token = useAccessToken();
    const qc = useQueryClient();

    const [tab, setTab] = useState<Tab>(isFolderMode ? "mine" : "recent");
    // Breadcrumb trail below the tab's root (empty = the tab itself).
    const [path, setPath] = useState<DriveFolderRef[]>([]);
    const [query, setQuery] = useState("");
    const [debounced, setDebounced] = useState("");
    const [selected, setSelected] = useState<Map<string, DriveItem>>(new Map());
    const [attachMode, setAttachMode] = useState<DriveAttachMode>(loadMode);
    const [navError, setNavError] = useState<string | null>(null);
    const [newFolderName, setNewFolderName] = useState<string | null>(null);
    const [creating, setCreating] = useState(false);
    const listRef = useRef<HTMLDivElement>(null);

    // Fresh state every time the dialog opens.
    useEffect(() => {
        if (!open) return;
        setTab(isFolderMode ? "mine" : "recent");
        setPath([]);
        setQuery("");
        setDebounced("");
        setSelected(new Map());
        setNavError(null);
        setNewFolderName(null);
    }, [open, isFolderMode]);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(query.trim()), 250);
        return () => clearTimeout(t);
    }, [query]);

    const searching = !isFolderMode && debounced.length > 0;
    const currentFolder = path.length > 0 ? path[path.length - 1]! : null;
    const enabled = open && Boolean(token);

    const recentQ = useQuery({
        queryKey: ["drive", "recent"],
        queryFn: () => driveApi.recent(token),
        enabled: enabled && tab === "recent" && !searching
    });
    const sharedQ = useQuery({
        queryKey: ["drive", "shared-with-me"],
        queryFn: () => driveApi.sharedWithMe(token),
        enabled: enabled && tab === "shared" && path.length === 0 && !searching
    });
    const childrenQ = useQuery({
        queryKey: ["drive", "children", currentFolder?.id ?? null],
        queryFn: () => driveApi.listChildren(currentFolder?.id ?? null, token),
        enabled: enabled && !searching && (tab === "mine" || (tab === "shared" && path.length > 0))
    });
    const searchQ = useQuery({
        queryKey: ["drive", "search", debounced],
        queryFn: () => driveApi.search(debounced, token),
        enabled: enabled && searching
    });

    const active = searching
        ? searchQ
        : tab === "recent"
            ? recentQ
            : tab === "shared" && path.length === 0
                ? sharedQ
                : childrenQ;

    const items: DriveItem[] = useMemo(() => {
        let rows: DriveItem[];
        if (searching) rows = searchQ.data ?? [];
        else if (tab === "recent") rows = (recentQ.data ?? []).filter((i) => i.kind === "File");
        else if (tab === "shared" && path.length === 0) rows = (sharedQ.data ?? []).map((s) => s.item);
        else rows = childrenQ.data ?? [];
        if (isFolderMode) rows = rows.filter((i) => i.kind === "Folder");
        // Folders first, then by name — Drive already sorts children this way,
        // recent/shared/search don't.
        if (tab !== "recent" || searching) {
            rows = [...rows].sort((a, b) =>
                a.kind === b.kind ? a.name.localeCompare(b.name) : a.kind === "Folder" ? -1 : 1);
        }
        return rows;
    }, [searching, searchQ.data, tab, recentQ.data, sharedQ.data, childrenQ.data, path.length, isFolderMode]);

    const switchTab = (next: Tab) => {
        setTab(next);
        setPath([]);
        setQuery("");
        setDebounced("");
        setNavError(null);
        setNewFolderName(null);
    };

    const openFolder = async (folder: DriveItem) => {
        setNavError(null);
        if (!searching && tab !== "recent") {
            setPath((p) => [...p, { id: folder.id, parentId: folder.parentId, name: folder.name }]);
            return;
        }
        // From search / recent: rebuild the breadcrumb from the server's chain.
        try {
            const chain = await driveApi.ancestors(folder.id, token);
            setTab(folder.ownerEmail ? "shared" : "mine");
            setPath(chain);
            setQuery("");
            setDebounced("");
        } catch (err) {
            setNavError(err instanceof Error ? err.message : String(err));
        }
    };

    const toggle = (item: DriveItem) => {
        setSelected((prev) => {
            const next = new Map(prev);
            if (next.has(item.id)) next.delete(item.id);
            else next.set(item.id, item);
            return next;
        });
    };

    // Arrow / Home / End move focus between rows; Space/Enter is the row
    // button's own click (toggle a file, open a folder).
    const onListKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
        if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
        const rows = Array.from(listRef.current?.querySelectorAll<HTMLElement>("[data-drive-row]") ?? []);
        if (rows.length === 0) return;
        e.preventDefault();
        const idx = rows.indexOf(document.activeElement as HTMLElement);
        let next = 0;
        if (e.key === "ArrowDown") next = idx < 0 ? 0 : Math.min(rows.length - 1, idx + 1);
        else if (e.key === "ArrowUp") next = idx < 0 ? 0 : Math.max(0, idx - 1);
        else if (e.key === "End") next = rows.length - 1;
        rows[next]?.focus();
    };

    const createFolder = async () => {
        const name = (newFolderName ?? "").trim();
        if (!name) return;
        setCreating(true);
        setNavError(null);
        try {
            const folder = await driveApi.createFolder(name, currentFolder?.id ?? null, token);
            await qc.invalidateQueries({ queryKey: ["drive", "children", currentFolder?.id ?? null] });
            setNewFolderName(null);
            setPath((p) => [...p, { id: folder.id, parentId: folder.parentId, name: folder.name }]);
        } catch (err) {
            setNavError(err instanceof Error ? err.message : String(err));
        } finally {
            setCreating(false);
        }
    };

    const picks = props.mode === "files"
        ? Array.from(selected.values()).map((i) => resolveDrivePick(i, attachMode, props.attachLimitBytes))
        : [];
    const actionable = picks.filter((p) => p.action !== "blocked");
    const notes = picks.filter((p) => p.reason);

    const confirm = () => {
        if (props.mode === "files") {
            if (actionable.length === 0) return;
            props.onPickFiles(picks);
        } else {
            props.onPickFolder(currentFolder
                ? { id: currentFolder.id, name: currentFolder.name }
                : { id: null, name: "My Drive" });
        }
        onOpenChange(false);
    };

    const tabRootLabel = tab === "recent" ? "Recent" : tab === "shared" ? "Shared with me" : "My Drive";
    const title = props.mode === "folder" ? (props.title ?? "Save to Drive") : "Attach from Drive";
    const description = props.mode === "folder"
        ? (props.description ?? "Choose a folder in My Drive.")
        : "Pick files to share as a link or attach as a copy.";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="flex h-[min(640px,92vh)] max-w-3xl flex-col gap-0 overflow-hidden p-0"
                // Keep compose's Ctrl+Enter / paste / drop handlers from seeing
                // events that bubble out of the portal.
                onKeyDown={(e) => e.stopPropagation()}
            >
                <div className="shrink-0 border-b px-5 pb-3 pt-4 pr-12">
                    <DialogTitle className="flex items-center gap-2 text-base">
                        <HardDrive className="h-4 w-4 text-primary" aria-hidden />
                        {title}
                    </DialogTitle>
                    <DialogDescription className="mt-1 text-xs">{description}</DialogDescription>
                </div>

                <div className="flex min-h-0 flex-1 flex-col sm:flex-row">
                    {/* Tabs — a left rail on wide screens, a row on phones. */}
                    {!isFolderMode ? (
                        <nav
                            role="tablist"
                            aria-label="Drive locations"
                            className="flex shrink-0 gap-1 overflow-x-auto border-b p-2 sm:w-44 sm:flex-col sm:border-b-0 sm:border-r"
                        >
                            {([
                                ["recent", "Recent", Clock],
                                ["mine", "My Drive", HardDrive],
                                ["shared", "Shared with me", Users]
                            ] as const).map(([id, label, Icon]) => (
                                <button
                                    key={id}
                                    type="button"
                                    role="tab"
                                    aria-selected={tab === id && !searching}
                                    onClick={() => switchTab(id)}
                                    className={cn(
                                        "flex shrink-0 items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                        tab === id && !searching
                                            ? "bg-primary/10 font-medium text-primary"
                                            : "text-foreground/80 hover:bg-accent"
                                    )}
                                >
                                    <Icon className="h-4 w-4" aria-hidden />
                                    {label}
                                </button>
                            ))}
                        </nav>
                    ) : null}

                    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b px-3 py-2">
                            {!isFolderMode ? (
                                <div className="relative min-w-[10rem] flex-1">
                                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
                                    <Input
                                        type="search"
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        placeholder="Search Drive"
                                        aria-label="Search Drive"
                                        className="h-8 pl-8 text-sm"
                                        autoFocus
                                    />
                                </div>
                            ) : (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setNewFolderName((v) => (v === null ? "" : null))}
                                    aria-expanded={newFolderName !== null}
                                >
                                    <FolderPlus className="h-4 w-4" /> New folder
                                </Button>
                            )}
                        </div>

                        {/* Breadcrumbs */}
                        <nav aria-label="Folder path" className="flex shrink-0 items-center gap-0.5 overflow-x-auto px-3 py-1.5 text-xs text-muted-foreground">
                            {searching ? (
                                <span>Search results for &ldquo;{debounced}&rdquo;</span>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => setPath([])}
                                        className={cn("shrink-0 rounded px-1 py-0.5 hover:bg-accent hover:text-foreground", path.length === 0 && "font-medium text-foreground")}
                                        aria-current={path.length === 0 ? "location" : undefined}
                                    >
                                        {tabRootLabel}
                                    </button>
                                    {path.map((f, i) => (
                                        <span key={f.id} className="flex shrink-0 items-center gap-0.5">
                                            <ChevronRight className="h-3 w-3" aria-hidden />
                                            <button
                                                type="button"
                                                onClick={() => setPath((p) => p.slice(0, i + 1))}
                                                className={cn("max-w-[12rem] truncate rounded px-1 py-0.5 hover:bg-accent hover:text-foreground", i === path.length - 1 && "font-medium text-foreground")}
                                                aria-current={i === path.length - 1 ? "location" : undefined}
                                            >
                                                {f.name}
                                            </button>
                                        </span>
                                    ))}
                                </>
                            )}
                        </nav>

                        {isFolderMode && newFolderName !== null ? (
                            <form
                                className="flex shrink-0 items-center gap-2 px-3 pb-2"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    void createFolder();
                                }}
                            >
                                <Input
                                    value={newFolderName}
                                    onChange={(e) => setNewFolderName(e.target.value)}
                                    placeholder="Folder name"
                                    aria-label="New folder name"
                                    className="h-8 text-sm"
                                    maxLength={200}
                                    autoFocus
                                />
                                <Button type="submit" size="sm" disabled={creating || !newFolderName.trim()}>
                                    {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                    Create
                                </Button>
                            </form>
                        ) : null}

                        {navError ? (
                            <p className="mx-3 mb-2 rounded border border-destructive/40 bg-destructive/10 px-2.5 py-1.5 text-xs text-destructive" role="alert">
                                {navError}
                            </p>
                        ) : null}

                        <div
                            ref={listRef}
                            onKeyDown={onListKeyDown}
                            role="list"
                            aria-label={searching ? "Search results" : tabRootLabel}
                            aria-busy={active.isFetching}
                            className="min-h-0 flex-1 overflow-y-auto px-2 pb-2"
                        >
                            {active.isPending && active.fetchStatus !== "idle" ? (
                                <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
                                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…
                                </div>
                            ) : active.isError ? (
                                <p className="px-2 py-10 text-center text-sm text-destructive">
                                    Couldn&rsquo;t load Drive: {active.error instanceof Error ? active.error.message : "unknown error"}
                                </p>
                            ) : items.length === 0 ? (
                                <p className="px-2 py-10 text-center text-sm text-muted-foreground">
                                    {searching
                                        ? "No files match your search."
                                        : isFolderMode
                                            ? "No folders here — save here, or create a new folder."
                                            : tab === "shared" && path.length === 0
                                                ? "Nothing has been shared with you yet."
                                                : "This folder is empty."}
                                </p>
                            ) : (
                                items.map((item) => (
                                    <DriveRow
                                        key={item.id}
                                        item={item}
                                        selectable={!isFolderMode && item.kind === "File"}
                                        selected={selected.has(item.id)}
                                        onToggle={() => toggle(item)}
                                        onOpen={() => void openFolder(item)}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                </div>

                <div className="shrink-0 space-y-2 border-t bg-muted/30 px-4 py-3">
                    {props.mode === "files" ? (
                        <>
                            <div role="radiogroup" aria-label="How to add the files" className="flex flex-wrap items-center gap-2 text-xs">
                                <span className="font-medium text-muted-foreground">Add as</span>
                                {([
                                    ["link", "Share as link", Link2],
                                    ["copy", "Attach as a copy", Paperclip]
                                ] as const).map(([id, label, Icon]) => (
                                    <button
                                        key={id}
                                        type="button"
                                        role="radio"
                                        aria-checked={attachMode === id}
                                        onClick={() => {
                                            setAttachMode(id);
                                            saveMode(id);
                                        }}
                                        className={cn(
                                            "flex items-center gap-1.5 rounded-full border px-2.5 py-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                            attachMode === id
                                                ? "border-primary bg-primary/10 font-medium text-primary"
                                                : "bg-background text-foreground/80 hover:bg-accent"
                                        )}
                                    >
                                        <Icon className="h-3.5 w-3.5" aria-hidden />
                                        {label}
                                    </button>
                                ))}
                            </div>
                            {notes.length > 0 ? (
                                <ul className="max-h-20 space-y-0.5 overflow-y-auto text-[11px]" aria-live="polite">
                                    {notes.map((p) => (
                                        <li
                                            key={p.item.id}
                                            className={cn("flex items-start gap-1.5", p.action === "blocked" ? "text-destructive" : "text-amber-700 dark:text-amber-300")}
                                        >
                                            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                                            <span className="min-w-0">
                                                <span className="font-medium">{p.item.name}:</span> {p.reason}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                        </>
                    ) : null}
                    <div className="flex items-center justify-between gap-2">
                        <span className="min-w-0 truncate text-xs text-muted-foreground">
                            {props.mode === "files"
                                ? selected.size === 0
                                    ? "No files selected"
                                    : `${selected.size} selected`
                                : `Saving to ${currentFolder?.name ?? "My Drive"}`}
                        </span>
                        <div className="flex shrink-0 gap-2">
                            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                onClick={confirm}
                                disabled={props.mode === "files" ? actionable.length === 0 : false}
                            >
                                {props.mode === "files"
                                    ? actionable.length > 1 ? `Insert ${actionable.length} files` : "Insert"
                                    : (props.confirmLabel ?? "Save here")}
                            </Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function DriveRow({
    item,
    selectable,
    selected,
    onToggle,
    onOpen
}: {
    item: DriveItem;
    selectable: boolean;
    selected: boolean;
    onToggle: () => void;
    onOpen: () => void;
}) {
    const isFolder = item.kind === "Folder";
    const viewOnly = !canShareItem(item);
    const noDownload = !canDownloadItem(item);
    const updated = new Date(item.updatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    const meta = [
        isFolder ? "Folder" : formatDriveBytes(item.sizeBytes),
        updated,
        item.ownerEmail ? item.ownerEmail : null
    ].filter(Boolean).join(" · ");

    return (
        <div role="listitem">
            <button
                type="button"
                data-drive-row
                onClick={isFolder ? onOpen : selectable ? onToggle : undefined}
                role={selectable ? "checkbox" : undefined}
                aria-checked={selectable ? selected : undefined}
                aria-label={isFolder ? `Open folder ${item.name}` : undefined}
                className={cn(
                    "flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    selected ? "bg-primary/10" : "hover:bg-accent/60"
                )}
            >
                {selectable ? (
                    <span
                        className={cn(
                            "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                            selected ? "border-primary bg-primary text-primary-foreground" : "border-input bg-background"
                        )}
                        aria-hidden
                    >
                        {selected ? <Check className="h-3 w-3" /> : null}
                    </span>
                ) : (
                    <span className="h-4 w-4 shrink-0" aria-hidden />
                )}
                <FileTypeIcon fileName={item.name} isFolder={isFolder} className="h-8 w-8" />
                <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium text-foreground">{item.name}</span>
                    <span className="block truncate text-[11px] text-muted-foreground">{meta}</span>
                </span>
                {!isFolder && viewOnly ? (
                    <span className="hidden shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground sm:inline">View only</span>
                ) : null}
                {!isFolder && noDownload ? (
                    <span className="hidden shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground sm:inline">No download</span>
                ) : null}
                {isFolder ? <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden /> : null}
            </button>
        </div>
    );
}
